import { Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Typography, useMediaQuery } from '@mui/material'
import { LockOutlined } from '@mui/icons-material'
import { TooltipHover } from '../../../presentation-components/miscellaneous-components/tooltip-hover'
import { partnerManagedTooltip } from '../../../../shared/utils/partner-managed-utils'
import { useContext, useEffect, useState } from 'react';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import RuleService from '../../../../shared/services/rule/rule.service';
import { IPostRule, IRule } from '../../../../shared/services/rule/rule.model';
import gaiaTheme from '../../../../root-components/theme';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { SettingsMobileNavbar } from '../../../../root-components/navigation/settings-mobile-navbar';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { LoadingPage } from '../../loading-page';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { RuleDialog } from '../../../dialogs/settings/data-import/rule-dialog';
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar';
import { useDebounce } from '../../../../hooks/use-debounce-hook';
import { formatRuleEmissions, formatRuleFactorExample, formatRuleQuantity } from '../../../../shared/utils/rule-impact-utils';

export const DataImportRulesPage = () => {
  //Services
  const ruleService = new RuleService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [rules, setRules] = useState<IRule[]>()
  const [ruleId, setRuleId] = useState<string>()
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [errorCode, setErrorCode] = useState<number>()
  const [errorMessage, setErrorMessage] = useState<string>()
  //Filter State
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Use Effects
  useEffect(() => {
    getAllRules()
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  useEffect(() => {
    if (inputPage === 0) {
      setChange(c => c + 1)
    } else {
      setInputPage(0)
    }
  }, [debouncedSearchQuery, typeFilter])

  const getAllRules = () => {
    ruleService.getAllWithPagination(inputPage, perPage, orderBy, order, debouncedSearchQuery || undefined, typeFilter || undefined, true)
      .then(response => {
        setIsLoading(false)
        setTotalRecords(response.count);
        setRules(response.rows)
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }
  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'value', numeric: false, label: 'Value', sortable: true, isHeading: true },
    { id: 'conversion', numeric: false, label: 'Conversion', sortable: false },
    { id: 'conversionSource', numeric: false, label: 'Conversion Factor Source', sortable: true },
    { id: 'type', numeric: false, label: 'Type', sortable: false },
    { id: 'usage', numeric: false, label: 'From Imports', sortable: false },
  ];

  // Only reserve the lock column once something on the page actually needs it, so a table
  // with no partner-managed rules keeps its original alignment.
  const hasPartnerManagedRules = (rules ?? []).some(rule => rule.isPartnerManaged)

  // The factor line under the conversion: the worked example, so a factor in the wrong unit or
  // the wrong order of magnitude shows itself ("100 litres = 274.93 kgCO2e" for AdBlue).
  const renderFactor = (rule: IRule) => {
    if (!rule.impact) return null
    const factor = rule.impact.factor
    if (factor) {
      return <Typography variant='caption' color='text.secondary'>{formatRuleFactorExample(factor)}</Typography>
    }
    if (rule.type === 'Vehicle Rule') {
      return <Typography variant='caption' color='text.secondary'>No default factor</Typography>
    }
    return <Typography variant='caption' color='error.main'>No factor found for the current year</Typography>
  }

  const renderUsage = (rule: IRule) => {
    const usage = rule.impact?.usage
    if (!usage) return ''
    if (rule.type === 'Vehicle Rule') {
      const vehicles = usage.vehicles ?? 0
      return vehicles > 0 ? `${vehicles} vehicle${vehicles === 1 ? '' : 's'}` : <Typography variant='body2' color='text.secondary'>Not used yet</Typography>
    }
    if (!usage.records) {
      return <Typography variant='body2' color='text.secondary'>Not used yet</Typography>
    }
    return (
      <Stack>
        <Typography variant='body2'>{formatRuleQuantity(usage, rule.measurementUnitName)}</Typography>
        <Typography variant='caption' color='text.secondary'>{formatRuleEmissions(usage)}</Typography>
      </Stack>
    )
  }

  const conversionLabel = (rule: IRule) => rule.type === 'activity-based emission factor'
    ? `${rule.groupName}, ${rule.categoryName}, ${rule.detailLevelOne}, ${rule.detailLevelTwo}${rule.detailLevelThree ? `, ${rule.detailLevelThree}` : ''}${rule.detailLevelFour ? `, ${rule.detailLevelFour}` : ''}${rule.detailLevelFive ? `, ${rule.detailLevelFive}` : ''}, ${rule.measurementUnitName}`
    : rule.type === 'Vehicle Rule' ? (rule.vehicleType ?? '') : ''

  const rows = !rules ? [] : rules.map((rule) => {
    return {
      id: rule.id,
      // A rule pushed from a partner's library is marked with a lock rather than a chip --
      // the row is already busy, and the lock reads at a glance. The tooltip carries the
      // detail: who it came from, and whether it is theirs to change.
      //
      // The lock sits in a fixed-width slot to the LEFT of the name, and that slot is
      // rendered on every row once any rule on the page is partner-managed. That keeps the
      // locks aligned down the column and the names aligned with each other, instead of the
      // lock trailing a name of variable length and wrapping onto the next line. Tables with
      // no partner-managed rules at all are left exactly as they were, with no empty indent.
      name: (
        <Stack direction='row' alignItems='center' sx={{ gap: 0.75, flexWrap: 'wrap' }}>
          <Stack direction='row' alignItems='center' sx={{ gap: 0.75, flexWrap: 'nowrap', minWidth: 0 }}>
            {hasPartnerManagedRules && (
              <Box sx={{ width: 18, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
                {rule.isPartnerManaged && (
                  <TooltipHover text={partnerManagedTooltip(rule)} backgroundColor='white' placement='top'>
                    {/* Wrapped because TooltipHover needs a ref-forwarding child, and a bare
                        svg is not a reliable hover target across browsers. */}
                    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                      <LockOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
                    </Box>
                  </TooltipHover>
                )}
              </Box>
            )}
            <Typography variant='body2'>{rule.name}</Typography>
          </Stack>
          {rule.editedByClient &&
            <Chip size='small' variant='outlined' color='info' label='Edited here' sx={{ flexShrink: 0 }} />}
        </Stack>
      ),
      value: rule.value,
      conversion: (
        <Stack>
          <Typography variant='body2'>{conversionLabel(rule)}</Typography>
          {renderFactor(rule)}
        </Stack>
      ),
      conversionSource: rule.conversionSource?.source ?? '',
      type: rule.type === 'activity-based emission factor' ? 'Activity-Based Conversion Factor' : rule.type === 'Vehicle Rule' ? 'Vehicle Rule' : '',
      usage: renderUsage(rule),
      action: (
        // Edit and Delete stay in the menu but go grey when the partner has locked the rule:
        // dropping them would leave an empty menu behind a ⋯ button that still renders, and
        // a control that silently disappears reads as a bug rather than as a permission.
        // canClientEdit is true for every ordinary rule, so unmanaged rows are unaffected.
        <TableActionButtons
          id={rule.id}
          includeEdit={true}
          includeDelete={true}
          disableButton={rule.canClientEdit === false}
          onEdit={() => editClickHandler(rule.id)}
          onDelete={() => deleteClickHandler(rule.id, rule.ruleInUse)}
        />
      )
    }
  })

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const editClickHandler = (ruleId: string) => {
    setRuleId(ruleId)
    setOpenDialog(true)
  }

  const deleteClickHandler = (ruleId: string, ruleInUse: boolean) => {
    userNotificationsContext.openDialog({
      active: true,
      title: ruleInUse ? 'Action Required' : 'Confirmation',
      message: ruleInUse ? `You cannot delete this rule because it has been used or is associated with other records. Please review and update any related items before trying again.` : `Are you sure you want to delete this rule? This action cannot be undone.`,
      buttonCopy: ruleInUse ? 'Ok' : 'Continue',
      cancelButtonCopy: 'Cancel',
      hideCancelButton: ruleInUse ? true : false
    })
      .then(response => {
        if (response && !ruleInUse) {
          ruleService.delete(ruleId)
            .then(response => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
              getAllRules()
            }).catch(err => {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              console.log(err)
            })
        }
      })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
        console.log(err)
      })
  }

  //Dialog Event Handlers
  const submitDialogHandler = (values: IPostRule, resetForm: () => void, isEdit: boolean,) => {
    if (!isEdit) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        ruleService.create(values)
          .then(() => {
            getAllRules()
            resetForm();
            setOpenDialog(false)
            userNotificationsContext.openSnackbar({ open: true, message: `Saved successfully` });
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            }
            console.log(err)
            setIsDialogSubmitting(false)
          })
      }
    }
    if (isEdit && ruleId) {
      if (!isDialogSubmitting) {
        setIsDialogSubmitting(true)
        ruleService.edit(ruleId, values)
          .then(response => {
            getAllRules()
            resetForm();
            setOpenDialog(false)
            setRuleId('')
            userNotificationsContext.openSnackbar({ open: true, message: `Updated successfully` });
            setIsDialogSubmitting(false)
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            }
            console.log(err)
            setIsDialogSubmitting(false)
          })
      }
    }
  }

  const closeDialogHandler = () => {
    setOpenDialog(false)
    setRuleId('')
  }

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>}
      <PageHeading
        title='Custom Rules'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => { setOpenDialog(true) }}
        actionButtonText='Add Rule'
        hasTabs={false}
      />
      <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </Box>
        <FormControl size='small' sx={{ minWidth: 220 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label='Type'
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='emission-activity'>Activity-Based Conversion Factor</MenuItem>
            <MenuItem value='vehicle'>Vehicle Rule</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <LoadingPage pageReady={!isLoading}>
        {rules && <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />}
      </LoadingPage>
      {!ruleId && openDialog &&
        <RuleDialog
          onSubmit={submitDialogHandler}
          errorCode={errorCode}
          errorMessage={errorMessage}
          open={openDialog}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDialogHandler}
          isEdit={false} />}
      {ruleId && openDialog &&
        <RuleDialog
          isSubmitting={isDialogSubmitting}
          onSubmit={submitDialogHandler}
          errorCode={errorCode}
          errorMessage={errorMessage}
          open={openDialog}
          closeHandler={closeDialogHandler}
          isEdit={true}
          ruleId={ruleId} />}
    </PageOuterContainer>
  )
}